import * as React from 'react'
import { ClickOutside } from '../../utils/clickOutside'
import { getUserAgent } from '../../utils/userAgent'
import { DataLayer } from '../../utils/datalayer'

export type QuickUtilsState = {
  isDropdownOpen: boolean
  copyCompleted: boolean
}

export type QuickUtilsProps = {
  downloadLink?: string
  pageTitle: string
  shareLabel: string
  isDropdownOpen: boolean
  copyCompleted: boolean
  onDropdownToggle: () => void
  onDropdownClose: () => void
  onCopy: () => void
  onShare?: (shareType: DataLayer.ShareType) => void
}
export const QuickUtilitiesMenu = (p: QuickUtilsProps) => (
  <section className={`page-header__utilities read-utils ru remprint rs_skip`} aria-label="Voorlezen, printen en delen">
    <ul className="qu__list">
      {p.downloadLink && (
      <li className="qu__item">
        <DownloadAndPrintLayout downloadLink={p.downloadLink} />
      </li>
      )}

      <li className="qu__item ">
        <ShareWithDropdownLayout {...p} />
      </li>
    </ul>
  </section>
)

export const DownloadAndPrintLayout = (props: { downloadLink: string }) => (
  <a href={props.downloadLink} target="_blank" className="link--has-icon">
    <span className="link__label">Download en print</span>
    <svg className="link__icon" aria-hidden="true" width="24px" height="24px">
      <use xlinkHref="#icon--SG-download"></use>
    </svg>
  </a>
)

const ShareWithDropdownLayout = (p: QuickUtilsProps) => {
  const dropdownRef = React.useRef<HTMLDivElement>(null)
  const dropdownButtonRef = React.useRef<HTMLButtonElement>(null)

  // Close Dropdown menu on blur
  const closeOnBlur = () => {
    if (dropdownRef.current && !dropdownRef.current.matches(':focus-within')) {
      p.onDropdownClose()
    }
  }

  // Close Dropdown menu on Escape
  React.useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        p.onDropdownClose()
      }
    }

    if (p.isDropdownOpen) {
      window.addEventListener('keydown', keyDownHandler, { passive: true })
    }
    return () => {
      window.removeEventListener('keydown', keyDownHandler)
    }
  }, [p.isDropdownOpen])

  return (
    <div ref={dropdownRef}>
      <button
        onClick={p.onDropdownToggle}
        aria-controls="a11y-share-toggle"
        aria-expanded={p.isDropdownOpen}
        className="link--has-icon"
        ref={dropdownButtonRef}
      >
        <svg className="link__icon" aria-hidden="true" width="24px" height="24px">
          <use xlinkHref="#icon--SG-share"></use>
        </svg>
        <span className="link__label text--s">{p.shareLabel}</span>
      </button>

      <ClickOutside
        as="div"
        id="a11y-share-toggle"
        hidden={!p.isDropdownOpen}
        aria-hidden={!p.isDropdownOpen}
        className="share"
        onClickOutside={p.onDropdownClose}
        exclude={dropdownRef}
      >
        <ul className="share__container">
          {socials.map((item) => (
            <li className="share__item" key={item.title}>
              <a
                className="share__item--link"
                onClick={() => p.onShare?.(item.shareType)}
                href={item.href(window.location.href, `${p.pageTitle}`)}
                target="_blank"
                onBlur={closeOnBlur}
              >
                <svg className="link__icon" aria-hidden="true" width="20px" height="20px">
                  <use xlinkHref={item.icon}></use>
                </svg>
                <span>{item.title}</span>
              </a>
            </li>
          ))}
          <li className="share__item" key={copyLink.title(false)}>
            <button
              className="share__item--link"
              onClick={(e) => {
                e.preventDefault()
                if (!p.copyCompleted && navigator?.clipboard) {
                  navigator.clipboard.writeText(window.location.href)
                }
                p.onCopy()
                p.onShare?.('copy_link')
              }}
              onBlur={closeOnBlur}
            >
              <svg className="link__icon" aria-hidden="true" width="20px" height="20px">
                <use xlinkHref={copyLink.icon}></use>
              </svg>
              <span>{copyLink.title(p.copyCompleted)}</span>
            </button>
          </li>
        </ul>
      </ClickOutside>
    </div>
  )
}

const socials: { href: (url: string, subject?: string) => string; title: string; icon: string; shareType: DataLayer.ShareType }[] = [
  {
    href: (url: string, subject?: string) =>
      (getUserAgent() == 'Mobile' ? `whatsapp://` : `https://web.whatsapp.com/`) + `send?text=Hierbij de link naar informatie over "${subject}" op de website van het Spaarne Gasthuis: ${url}`,
    title: 'Whatsapp',
    icon: '#icon--whatsapp',
    shareType: 'whatsapp',
  },
  {
    href: (url: string, subject?: string) => `mailto:?subject=Informatie over "${subject}"&body=Hierbij de link naar informatie over "${subject}" op de website van het Spaarne Gasthuis: ${url}`,
    title: 'E-mail',
    icon: '#icon--mail',
    shareType: 'email',
  },
]

const copyLink = {
  title: (copied: boolean) => (copied ? 'Gekopieerd' : 'Kopiëer link'),
  icon: '#icon--mail',
}
